import React, { useMemo } from 'react';
import { useT } from 'talkr';
import s from './index.module.scss';
import { useAppStore } from '../../store';

type Section = {
  id: string;
  fill: number;
};

function SmartProgressBar(): React.ReactElement | null {
  const currentStepIndex = useAppStore((state) => state.currentStepIndex);
  const totalSteps = useAppStore((state) => state.totalSteps);
  const separationStepIndexes = useAppStore((state) => state.separationStepIndexes);
  const hideHeaderStepIndexes = useAppStore((state) => state.hideHeaderStepIndexes);
  const hideProgressStepIndexes = useAppStore((state) => state.hideProgressStepIndexes);
  const progressTitlesBySection = useAppStore((state) => state.progressTitlesBySection);
  const { T } = useT();

  const { sections, currentSectionIndex } = useMemo<{
    sections: Section[];
    currentSectionIndex: number;
  }>(() => {
    if (
      currentStepIndex == null
      || totalSteps == null
      || !Array.isArray(separationStepIndexes)
    ) {
      return { sections: [], currentSectionIndex: -1 };
    }

    const hideHeaderSet = new Set(hideHeaderStepIndexes || []);
    const hideProgressSet = new Set(hideProgressStepIndexes || []);
    const separationSet = new Set(separationStepIndexes);

    const sectionSteps: number[][] = [];
    let currentSection: number[] = [];

    for (let i = 0; i < totalSteps; i += 1) {
      const isProgressStep = !hideHeaderSet.has(i) && !hideProgressSet.has(i);

      if (isProgressStep) {
        currentSection.push(i);
      }

      if (separationSet.has(i)) {
        if (currentSection.length) {
          sectionSteps.push(currentSection);
        }
        currentSection = [];
      }
    }

    if (currentSection.length) {
      sectionSteps.push(currentSection);
    }

    const calculatedSectionIndex = sectionSteps.findIndex(
      (section) => section.includes(currentStepIndex),
    );

    const calculatedSections: Section[] = sectionSteps.map((section, sectionIndex) => {
      let fill = 0;

      if (sectionIndex < calculatedSectionIndex) {
        fill = 100;
      } else if (sectionIndex === calculatedSectionIndex) {
        const passedInSection = section.filter((i) => i <= currentStepIndex).length;
        fill = (passedInSection / section.length) * 100;
      }

      return {
        id: `section-${sectionIndex}`,
        fill: Math.min(Math.max(fill, 0), 100),
      };
    });

    return {
      sections: calculatedSections,
      currentSectionIndex: calculatedSectionIndex,
    };
  }, [
    currentStepIndex,
    totalSteps,
    separationStepIndexes,
    hideHeaderStepIndexes,
    hideProgressStepIndexes,
  ]);

  const titleKey = useMemo<string | null>(() => {
    if (
      !Array.isArray(progressTitlesBySection)
      || currentSectionIndex < 0
    ) {
      return null;
    }

    return progressTitlesBySection[currentSectionIndex] || null;
  }, [progressTitlesBySection, currentSectionIndex]);

  if (!sections.length) return null;

  return (
    <div className={s.wrapper}>
      <p className={s.title}>{titleKey ? T(titleKey) : null}</p>
      <div className={s.bar}>
        {sections.map((section) => (
          <div key={section.id} className={s.section}>
            <div
              className={s.sectionFill}
              style={{ width: `${section.fill}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default SmartProgressBar;
